import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Table,
    Button,
    Tag,
    Space,
    Radio,
    message,
    Input,
    Select,
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    SaveOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { DifficultyLevel, QuestionStatus } from './aqgEnums';
import { AlertType } from '@quyva/shared-ui';

const QuestionsModal = ({ open, onClose, repoId, fileName }) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [editingKey, setEditingKey] = useState('');

    useEffect(() => {
        if (repoId) {
            getQuestions();
        }
    }, [repoId]);

    const getQuestions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/aqg_routes/get_questions`,
                { params: { repoId } } // Axios way to handle query params
            );
            const data = response.data;
            // Transform the API response to match the expected format
            // Filter out rejected questions
            const formattedQuestions = data
                .filter((q) => q.status !== QuestionStatus.REJECTED)
                .map((q) => {
                    try {
                        let parsedOptions = q.options;
                        // Create a more robust options parsing function
                        if (typeof q.options === 'string') {
                            try {
                                parsedOptions = JSON.parse(
                                    q.options
                                        .replace(/'/g, '"')
                                        .replace(/None/g, 'null')
                                );
                            } catch (e) {
                                console.error(
                                    'Failed to parse options:',
                                    q.options
                                );
                                return { a: '', b: '', c: '', d: '' };
                            }
                        }

                        return {
                            ID: q.id,
                            question: q.question,
                            question_type: q.question_type.toUpperCase(),
                            difficulty_level: q.difficulty_level,
                            options: JSON.stringify(parsedOptions),
                            answer: q.answer,
                            bloom_level: q.bloom_level,
                            concept_name: q.concept_name,
                            Status: q.status,
                            timestamp: q.timestamp,
                        };
                    } catch (parseError) {
                        console.error('Error parsing question:', {
                            questionId: q.id,
                            rawOptions: q.options,
                            error: parseError,
                        });

                        // Return a simplified version of the question without parsed options
                        return {
                            ID: q.id,
                            question: q.question,
                            question_type: q.question_type.toUpperCase(),
                            difficulty_level: q.difficulty_level,
                            options:
                                '{"a":"Error parsing options","b":"","c":"","d":""}',
                            answer: '0',
                            bloom_level: q.bloom_level,
                            concept_name: q.concept_name,
                            Status: q.status,
                            timestamp: q.timestamp,
                        };
                    }
                });

            setQuestions(formattedQuestions);
            setLoading(false);
            message.success('Questions fetched successfully');
        } catch (error) {
            console.error('Error fetching questions:', error);
            message.error('Failed to fetch questions');
            setLoading(false);
        }
    };

    const isEditing = (record) => record.ID === editingKey;

    const edit = (record) => {
        setEditingKey(record.ID);
    };

    const cancel = () => {
        setEditingKey('');
    };

    const save = async (record) => {
        try {
            // Parse options object
            const optionsObj = parseOptions(record.options);

            // Build options string to match the exact format in DB
            const optionsStr = `{'a': "${optionsObj.a}", 'b': "${optionsObj.b}", 'c': "${optionsObj.c}", 'd': "${optionsObj.d}"}`;
            // Format the record back to API format
            const apiFormat = {
                id: record.ID,
                repo_id: repoId,
                doc_id: null,
                question: record.question,
                question_type: record.question_type.toLowerCase(),
                difficulty_level: record.difficulty_level,
                options: optionsStr,
                answer: record.answer,
                bloom_level: record.bloom_level,
                concept_name: record.concept_name,
                status: record.Status.toLowerCase(),
                timestamp: record.timestamp,
                answer_matched: true,
                distractor_ambiguity: true,
            };

            // Axios PUT request
            const response = await axios.put(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/aqg_routes/update_question`,
                apiFormat, // Axios automatically JSON-stringifies the body
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const updatedData = response.data;
            // Update local state with the response from server
            const newQuestions = questions.map((q) => {
                if (q.ID === record.ID) {
                    // Transform the API response back to component format
                    // Convert options from {'a': 'val1', 'b': 'val2'} format to array format
                    const optionsObj = JSON.parse(
                        updatedData.options.replace(/'/g, '"')
                    );
                    const optionsArray = [
                        optionsObj.a,
                        optionsObj.b,
                        optionsObj.c,
                        optionsObj.d,
                    ];

                    return {
                        ID: updatedData.id,
                        question: updatedData.question,
                        question_type: updatedData.question_type.toUpperCase(),
                        difficulty_level: updatedData.difficulty_level,
                        options: JSON.stringify(optionsArray),
                        answer: Object.keys(optionsObj)
                            .indexOf(updatedData.answer)
                            .toString(),
                        bloom_level: updatedData.bloom_level,
                        concept_name: updatedData.concept_name,
                        Status: updatedData.status,
                        timestamp: updatedData.timestamp,
                    };
                }
                return q;
            });

            setQuestions(newQuestions);
            setEditingKey('');
            message.success('Question updated successfully');
        } catch (error) {
            const errorMessage =
                error.response?.data?.error ||
                error.message ||
                'Failed to update question';
            console.error('Error updating question:', errorMessage);
            message.error(errorMessage);
        }
    };

    const parseOptions = (optionsStr) => {
        try {
            const parsed =
                typeof optionsStr === 'string'
                    ? JSON.parse(optionsStr)
                    : optionsStr;
            // If parsed is an array, convert to object format
            if (Array.isArray(parsed)) {
                return {
                    a: parsed[0] || '',
                    b: parsed[1] || '',
                    c: parsed[2] || '',
                    d: parsed[3] || '',
                };
            }
            return parsed;
        } catch (e) {
            console.error('Failed to parse options in EditableQuestion:', e);
            return { a: '', b: '', c: '', d: '' };
        }
    };

    const EditableQuestion = ({ question, editing }) => {
        // Initialize editedQuestion with parsed options
        const [editedQuestion, setEditedQuestion] = useState(() => {
            const parsedOptions = parseOptions(question.options);
            return {
                ...question,
                options: JSON.stringify(parsedOptions),
            };
        });

        // Update editedQuestion when the parent question changes
        useEffect(() => {
            if (editing) {
                // Only update non-options fields if we're editing
                setEditedQuestion((prev) => ({
                    ...question,
                    options: prev.options, // Preserve the current options
                }));
            } else {
                // If not editing, update everything
                const parsedOptions = parseOptions(question.options);
                setEditedQuestion({
                    ...question,
                    options: JSON.stringify(parsedOptions),
                });
            }
        }, [question, editing]);

        const handleOptionChange = (key, value) => {
            const currentOptions = parseOptions(editedQuestion.options);
            const newOptions = {
                ...currentOptions,
                [key]: value,
            };
            const updatedQuestion = {
                ...editedQuestion,
                options: JSON.stringify(newOptions),
            };
            setEditedQuestion(updatedQuestion);
            handleEditQuestion(updatedQuestion); // Update parent state
        };

        const handleQuestionChange = (value) => {
            const updatedQuestion = {
                ...editedQuestion,
                question: value,
            };
            setEditedQuestion(updatedQuestion);
            handleEditQuestion(updatedQuestion);
        };

        const options = parseOptions(editedQuestion.options);

        if (!editing) {
            return (
                <div className='mb-4 p-4 border rounded-lg bg-backgrounds-white'>
                    <div className='font-medium mb-2'>{question.question}</div>
                    <Radio.Group
                        onChange={(e) =>
                            handleAnswerSelect(question.ID, e.target.value)
                        }
                        value={selectedAnswers[question.ID]}
                    >
                        <Space direction='vertical'>
                            {Object.entries(options).map(([key, value]) => (
                                <Radio key={key} value={key}>
                                    {value}
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                </div>
            );
        }

        return (
            <div className='mb-4 p-4 border rounded-lg bg-backgrounds-white'>
                <div className='mb-4'>
                    <Input.TextArea
                        value={editedQuestion.question}
                        onChange={(e) => handleQuestionChange(e.target.value)}
                        autoSize
                        className='font-medium'
                    />
                </div>
                <Space direction='vertical' className='w-full'>
                    {Object.entries(options).map(([key, value]) => (
                        <Input
                            key={key}
                            value={value}
                            onChange={(e) =>
                                handleOptionChange(key, e.target.value)
                            }
                            prefix={<Radio disabled />}
                            placeholder={`Option ${key.toUpperCase()}`}
                        />
                    ))}
                </Space>
                <div className='mt-4 flex justify-end space-x-2'>
                    <Button
                        onClick={() => save(editedQuestion)}
                        type='primary'
                        icon={<SaveOutlined />}
                    >
                        Save
                    </Button>
                    <Button onClick={cancel} icon={<CloseOutlined />}>
                        Cancel
                    </Button>
                </div>
            </div>
        );
    };

    const handleAnswerSelect = (questionId, optionIndex) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [questionId]: optionIndex,
        });
    };

    const handleEditQuestion = (question) => {
        const newQuestions = questions.map((q) => {
            if (q.ID === question.ID) {
                return {
                    ...q,
                    ...question,
                };
            }
            return q;
        });
        setQuestions(newQuestions);
    };

    const handleDeleteQuestion = (questionId) => {
        Modal.confirm({
            title: 'Delete Question',
            content: 'Are you sure you want to reject this question?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const questionToUpdate = questions.find(
                        (q) => q.ID === questionId
                    );
                    if (!questionToUpdate) return;

                    // Prepare data for API
                    const apiFormat = {
                        id: questionToUpdate.ID,
                        repo_id: repoId,
                        doc_id: null,
                        question: questionToUpdate.question,
                        question_type:
                            questionToUpdate.question_type.toLowerCase(),
                        difficulty_level: questionToUpdate.difficulty_level,
                        options: questionToUpdate.options,
                        answer: questionToUpdate.answer,
                        bloom_level: questionToUpdate.bloom_level,
                        concept_name: questionToUpdate.concept_name,
                        status: QuestionStatus.REJECTED, // Set status to rejected
                        timestamp: questionToUpdate.timestamp,
                        answer_matched: true,
                        distractor_ambiguity: true,
                    };

                    // Call update API
                    // Axios PUT request
                    await axios.put(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/aqg_routes/update_question`,
                        apiFormat,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    );

                    // Update local state to remove the rejected question
                    const newQuestions = questions.filter(
                        (q) => q.ID !== questionId
                    );
                    setQuestions(newQuestions);
                    message.success('Question rejected successfully');
                } catch (error) {
                    const errorMessage =
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to reject question';
                    console.error('Error rejecting question:', errorMessage);
                    message.error(errorMessage);
                }
            },
        });
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case QuestionStatus.VERIFIED:
                return AlertType.SUCCESS;
            case QuestionStatus.UNVERIFIED:
                return AlertType.WARNING;
            case QuestionStatus.REJECTED:
                return AlertType.ERROR;
            default:
                return 'default';
        }
    };

    const columns = [
        {
            title: 'Question & Options',
            key: 'question',
            render: (_, record) => (
                <EditableQuestion
                    question={record}
                    editing={isEditing(record)}
                />
            ),
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder='Search question'
                        value={selectedKeys[0]}
                        onChange={(e) =>
                            setSelectedKeys(
                                e.target.value ? [e.target.value] : []
                            )
                        }
                        onPressEnter={() => confirm()}
                        style={{
                            width: 188,
                            marginBottom: 8,
                            display: 'block',
                        }}
                    />
                    <Space>
                        <Button
                            type='primary'
                            onClick={() => confirm()}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Filter
                        </Button>
                        <Button
                            onClick={() => clearFilters()}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.question.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Concept Name',
            dataIndex: 'concept_name',
            key: 'concept_name',
            width: 150,
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder='Search concept'
                        value={selectedKeys[0]}
                        onChange={(e) =>
                            setSelectedKeys(
                                e.target.value ? [e.target.value] : []
                            )
                        }
                        onPressEnter={() => confirm()}
                        style={{
                            width: 188,
                            marginBottom: 8,
                            display: 'block',
                        }}
                    />
                    <Space>
                        <Button
                            type='primary'
                            onClick={() => confirm()}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Filter
                        </Button>
                        <Button
                            onClick={() => clearFilters()}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.concept_name
                    ?.toLowerCase()
                    .includes(value.toLowerCase()),
            render: (concept_name, record) => {
                const editable = isEditing(record);
                if (editable) {
                    return (
                        <Input
                            value={concept_name}
                            onChange={(e) => {
                                const updatedQuestion = {
                                    ...record,
                                    concept_name: e.target.value,
                                };
                                handleEditQuestion(updatedQuestion);
                            }}
                        />
                    );
                }
                return concept_name;
            },
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'Status',
            width: 120,
            filters: Object.values(QuestionStatus).map((status) => ({
                text: status,
                value: status,
            })),

            onFilter: (value, record) => record.Status === value,
            render: (status, record) => {
                const editable = isEditing(record);
                if (editable) {
                    return (
                        <Select
                            value={status || QuestionStatus.UNVERIFIED}
                            onChange={(value) => {
                                const updatedQuestion = {
                                    ...record,
                                    Status: value,
                                };
                                handleEditQuestion(updatedQuestion);
                            }}
                            style={{ width: '100%' }}
                        >
                            {Object.values(QuestionStatus).map((status) => (
                                <Select.Option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() +
                                        status.slice(1)}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }
                return (
                    <Tag
                        color={getStatusColor(
                            status || QuestionStatus.UNVERIFIED
                        )}
                    >
                        {status || QuestionStatus.UNVERIFIED}
                    </Tag>
                );
            },
        },
        {
            title: 'Difficulty',
            dataIndex: 'difficulty_level',
            key: 'difficulty_level',
            width: 100,
            filters: [
                { text: DifficultyLevel.EASY, value: DifficultyLevel.EASY },
                { text: DifficultyLevel.MEDIUM, value: DifficultyLevel.MEDIUM },
                { text: DifficultyLevel.HARD, value: DifficultyLevel.HARD },
            ],
            onFilter: (value, record) => record.difficulty_level === value,
            render: (difficulty, record) => {
                const editable = isEditing(record);

                if (editable) {
                    return (
                        <Select
                            value={record.difficulty_level}
                            onChange={(value) => {
                                const updatedQuestion = {
                                    ...record,
                                    difficulty_level: value,
                                };
                                handleEditQuestion(updatedQuestion);
                            }}
                            style={{ width: '100%' }}
                        >
                            {Object.values(DifficultyLevel).map((level) => (
                                <Select.Option key={level} value={level}>
                                    {level.charAt(0).toUpperCase() +
                                        level.slice(1)}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }

                return (
                    <Tag color={getDifficultyColor(difficulty)}>
                        {difficulty}
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => {
                const editable = isEditing(record);
                return (
                    <Space>
                        {!editable ? (
                            <Button
                                type='text'
                                icon={<EditOutlined />}
                                onClick={() => edit(record)}
                                disabled={editingKey !== ''}
                            />
                        ) : null}
                        <Button
                            type='text'
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteQuestion(record.ID)}
                            disabled={editable}
                        />
                    </Space>
                );
            },
        },
    ];

    const getDifficultyColor = (difficulty) => {
        switch (difficulty.toLowerCase()) {
            case DifficultyLevel.EASY:
                return AlertType.SUCCESS;
            case DifficultyLevel.MEDIUM:
                return AlertType.WARNING;
            case DifficultyLevel.HARD:
                return AlertType.ERROR;
            default:
                return 'default';
        }
    };

    const handleVerifyAll = async () => {
        Modal.confirm({
            title: 'Verify All Questions',
            content: 'Are you sure you want to verify all questions?',
            okText: 'Yes',
            okType: 'primary',
            cancelText: 'No',
            onOk: async () => {
                try {
                    // Call the new endpoint to verify all questions at once
                    await axios.put(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/verify_all_questions`,
                        null, // No body needed
                        {
                            params: { repoId }, // Query param
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    );
                    // Update local state
                    const updatedQuestions = questions.map((q) => ({
                        ...q,
                        Status: QuestionStatus.VERIFIED,
                    }));
                    setQuestions(updatedQuestions);
                    message.success('All questions verified successfully');
                } catch (error) {
                    const errorMessage =
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to verify all questions';
                    console.error('Error verifying questions:', errorMessage);
                    message.error(errorMessage);
                }
            },
        });
    };

    const hasUnverifiedQuestions = questions.some(
        (q) => q.Status !== QuestionStatus.VERIFIED
    );

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width='80%'
            title={`${fileName} Questions`}
            centered
            style={{ top: 20 }}
            bodyStyle={{
                height: '70vh',
                overflow: 'auto',
                padding: '24px',
            }}
            footer={[
                <Button
                    key='verify'
                    type='primary'
                    onClick={handleVerifyAll}
                    disabled={!hasUnverifiedQuestions}
                >
                    Verify All
                </Button>,
                <Button key='close' onClick={onClose}>
                    Close
                </Button>,
            ]}
        >
            <Table
                columns={columns}
                dataSource={questions}
                rowKey='ID'
                loading={loading}
                pagination={{
                    pageSize: 5,
                    position: ['bottomCenter'],
                    showSizeChanger: false,
                }}
                onChange={(pagination, filters, sorter) => {
                    console.log('Table params:', {
                        pagination,
                        filters,
                        sorter,
                    });
                }}
            />
        </Modal>
    );
};

export default QuestionsModal;
